"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollaborationAccessControl = void 0;
class CollaborationAccessControl {
    /**
     * Get user's role in a watchlist
     */
    static getUserRole(userId, watchlistOwnerId, collaborators = []) {
        if (userId === watchlistOwnerId) {
            return 'owner';
        }
        const collaborator = collaborators.find(c => c.uid === userId);
        if (collaborator) {
            return collaborator.role || 'editor';
        }
        return 'public';
    }
    /**
     * Get permissions for a user based on their role
     */
    static getPermissions(role) {
        const basePermissions = {
            canAddMovies: false,
            canRemoveMovies: false,
            canEditNotes: false,
            canEditName: false,
            canEditDescription: false,
            canChangePrivacy: false,
            canAddCollaborators: false,
            canRemoveCollaborators: false,
            canAssignAdminRole: false,
            canRemoveAdminRole: false,
            canRemoveSelf: false,
            canSeeCollaborators: true,
            canSeeLikes: false,
            canShare: true,
            canLike: true,
            canUnlike: true,
            canManageOtherCollaborators: false,
        };
        switch (role) {
            case 'owner':
                return Object.assign(Object.assign({}, basePermissions), { canAddMovies: true, canRemoveMovies: true, canEditNotes: true, canEditName: true, canEditDescription: true, canChangePrivacy: true, canAddCollaborators: true, canRemoveCollaborators: true, canAssignAdminRole: true, canRemoveAdminRole: true, canRemoveSelf: false, canSeeLikes: true, canManageOtherCollaborators: true });
            case 'admin':
                return Object.assign(Object.assign({}, basePermissions), { canAddMovies: true, canRemoveMovies: true, canEditNotes: true, canEditName: true, canEditDescription: true, canChangePrivacy: true, canAddCollaborators: true, canRemoveCollaborators: true, canAssignAdminRole: true, canRemoveAdminRole: true, canRemoveSelf: true, canSeeLikes: true, canManageOtherCollaborators: true });
            case 'editor':
                return Object.assign(Object.assign({}, basePermissions), { canAddMovies: true, canRemoveMovies: true, canEditNotes: true, canEditName: true, canEditDescription: true, canChangePrivacy: false, canAddCollaborators: false, canRemoveCollaborators: false, canAssignAdminRole: false, canRemoveAdminRole: false, canRemoveSelf: true, canSeeLikes: true, canManageOtherCollaborators: false });
            case 'public':
            default:
                return Object.assign(Object.assign({}, basePermissions), { canSeeLikes: true });
        }
    }
    /**
     * Check if user can perform a specific action on a collaborator
     */
    static canManageCollaborator(currentUserRole, targetUserRole, currentUserId, targetUserId) {
        const isSelf = currentUserId === targetUserId;
        if (currentUserRole === 'public') {
            return {
                canRemove: false,
                canPromoteToAdmin: false,
                canDemoteFromAdmin: false,
                canChangeSelf: false,
            };
        }
        const canChangeSelf = isSelf && currentUserRole !== 'owner';
        if (currentUserRole === 'owner') {
            return {
                canRemove: !isSelf,
                canPromoteToAdmin: !isSelf && targetUserRole === 'editor',
                canDemoteFromAdmin: !isSelf && targetUserRole === 'admin',
                canChangeSelf: false,
            };
        }
        if (currentUserRole === 'admin') {
            return {
                canRemove: (targetUserRole === 'editor') || isSelf,
                canPromoteToAdmin: targetUserRole === 'editor' && !isSelf,
                canDemoteFromAdmin: false,
                canChangeSelf,
            };
        }
        if (currentUserRole === 'editor') {
            return {
                canRemove: isSelf,
                canPromoteToAdmin: false,
                canDemoteFromAdmin: false,
                canChangeSelf,
            };
        }
        return {
            canRemove: false,
            canPromoteToAdmin: false,
            canDemoteFromAdmin: false,
            canChangeSelf: false,
        };
    }
    /**
     * Get user permissions for a specific watchlist
     */
    static getUserPermissions(user, watchlistOwnerId, collaborators = []) {
        if (!user) {
            return {
                role: 'public',
                permissions: this.getPermissions('public'),
            };
        }
        const role = this.getUserRole(user.uid, watchlistOwnerId, collaborators);
        const permissions = this.getPermissions(role);
        return { role, permissions };
    }
    /**
     * Check if a watchlist is collaborated (has collaborators)
     */
    static isCollaboratedList(collaborators = []) {
        return collaborators.length > 0;
    }
    /**
     * Check if a user can like/unlike a public watchlist
     */
    static canLikeWatchlist(user, watchlistOwnerId, isPublic = true) {
        if (!user)
            return false;
        if (user.uid === watchlistOwnerId)
            return false;
        return isPublic;
    }
    /**
     * Get role display information
     */
    static getRoleDisplay(role) {
        switch (role) {
            case 'owner':
                return {
                    label: 'Owner',
                    color: '#FF6B35',
                    description: 'Full control over the watchlist',
                };
            case 'admin':
                return {
                    label: 'Admin',
                    color: '#5856D6',
                    description: 'Can manage collaborators and settings',
                };
            case 'editor':
                return {
                    label: 'Editor',
                    color: '#8E8E93',
                    description: 'Can edit content but not settings',
                };
            default:
                return {
                    label: 'Viewer',
                    color: '#8E8E93',
                    description: 'Can view the watchlist',
                };
        }
    }
}
exports.CollaborationAccessControl = CollaborationAccessControl;
exports.default = CollaborationAccessControl;
